'use client'
import React from 'react'
import {
  Drawer,
  Typography,
  Button,
  Card,
  Row,
  Col,
  Empty,
  Spin,
  Progress,
  Divider,
  Tag,
  Table,
} from 'antd'
import {
  DownloadOutlined,
  FileTextOutlined,
  CloseOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import styled from 'styled-components'

const { Title, Text } = Typography

// Styled components for the drawer
const drawerStyles = {
  transcriptionContainer: {
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '6px',
    border: '1px solid #f0f0f0',
    marginTop: '8px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  transcriptionText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#333',
    wordWrap: 'break-word',
  },
  normalText: {
    color: '#333',
  },
  errorReplace: {
    backgroundColor: '#ffe7e7',
    color: '#d32f2f',
    padding: '2px 4px',
    borderRadius: '3px',
    fontWeight: '500',
    border: '1px solid #ffcdd2',
  },
  errorDelete: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '2px 4px',
    borderRadius: '3px',
    fontWeight: '500',
    border: '1px solid #bbdefb',
    textDecoration: 'line-through',
  },
  errorInsert: {
    backgroundColor: '#e8f5e8',
    color: '#388e3c',
    padding: '2px 4px',
    borderRadius: '3px',
    fontWeight: '500',
    border: '1px solid #c8e6c9',
  },
  errorLegend: {
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    marginBottom: '12px',
    border: '1px solid #e9ecef',
  },
  legendTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#495057',
    marginRight: '12px',
  },
  legendItems: {
    display: 'flex',
    gap: '16px',
    marginTop: '6px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    color: '#6c757d',
  },
}

// Enhanced styled components matching assessment-react
const DrawerContainer = styled.div`
  background: #fafafa;
  min-height: 100%;
`

const ContentSection = styled.div`
  margin: 0 8px 24px 8px;
`

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0 0 16px 0;
  padding: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 12px;
  }
`

// Aptitude specific styled components
const OverallScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
  }
`

const ScoreCard = styled.div`
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ScoreValue = styled.div`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const ScoreLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const ScoreSubtext = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
`

const SummaryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  padding: 16px;
  border-radius: 6px;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d9d9d9;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`

const SummaryLabel = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

const DifficultyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const DifficultyCard = styled.div`
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  border: 2px solid ${props => props.borderColor};
  background: ${props => props.bgColor};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const DifficultyLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 8px;
  }
`

const DifficultyScore = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const DifficultyPercentage = styled.div`
  font-size: 14px;
  color: #8c8c8c;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const ResponsiveTableContainer = styled.div`
  margin-bottom: 24px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  overflow: hidden;

  .ant-table {
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    background: #fafafa;
    border-bottom: 2px solid #e8e8e8;
    font-weight: 600;
    color: #262626;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 12px 8px;
  }

  .ant-table-tbody > tr > td {
    padding: 12px 8px;
    border-bottom: 1px solid #f0f0f0;
    color: #595959;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f9fafb;
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: #fafafa;
  }

  .ant-table-tbody > tr:nth-child(even):hover > td {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 12px;
    }

    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 4px;
    }

    .ant-table-thead > tr > th {
      font-size: 10px;
    }
  }
`

const ScoreDrawer = ({
  visible,
  onClose,
  assessment,
  reports,
  loading,
  error,
  onDownloadReport,
  onViewDetailedReport,
}) => {
  const getScoreColor = score => {
    if (score >= 70) return '#52c41a' // Green
    if (score >= 50) return '#1890ff' // Blue
    return '#ff4d4f' // Red
  }

  const getScoreLevel = score => {
    if (score >= 70) return 'Good'
    if (score >= 50) return 'Average'
    return 'Needs Work'
  }

  const findAssessmentReport = () => {
    if (!reports || !Array.isArray(reports) || !assessment) {
      return null
    }

    const assessmentId = assessment.assessment_assigned_id || assessment.id
    const matchedReport = reports.find(report => report.id === assessmentId)
    return matchedReport
  }

  const parseTranscriptionWithErrors = text => {
    if (!text) return []

    // Split text by error patterns {ErrorType}
    const errorPattern = /\{([^}]+)\}/g
    const parts = []
    let lastIndex = 0
    let match

    while ((match = errorPattern.exec(text)) !== null) {
      // Get text before the error marker
      const beforeError = text.substring(lastIndex, match.index)

      // Find the last word before the error
      const words = beforeError.trim().split(/\s+/)
      const lastWord = words.pop() || ''
      const textBeforeLastWord = words.join(' ')

      // Add text before the last word (if any)
      if (textBeforeLastWord) {
        parts.push({
          type: 'normal',
          text: textBeforeLastWord + ' ',
        })
      }

      // Add the error word with styling
      if (lastWord) {
        const errorType = match[1]
        let cssType = 'replace' // default

        if (errorType === 'Mispronunciation') {
          cssType = 'replace'
        } else if (errorType === 'Omission') {
          cssType = 'delete'
        } else if (errorType === 'Insertion') {
          cssType = 'insert'
        }

        parts.push({
          type: 'error',
          text: lastWord,
          errorType: errorType,
          cssType: cssType,
        })
      }

      lastIndex = match.index + match[0].length
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'normal',
        text: text.substring(lastIndex),
      })
    }

    return parts
  }

  const renderErrorLegend = () => (
    <div style={drawerStyles.errorLegend}>
      <span style={drawerStyles.legendTitle}>Error Types:</span>
      <div style={drawerStyles.legendItems}>
        <div style={drawerStyles.legendItem}>
          <span
            style={{
              ...drawerStyles.errorReplace,
              padding: '1px 4px',
              fontSize: '9px',
            }}
          >
            Replace
          </span>
          <span>Mispronunciation</span>
        </div>
        <div style={drawerStyles.legendItem}>
          <span
            style={{
              ...drawerStyles.errorDelete,
              padding: '1px 4px',
              fontSize: '9px',
            }}
          >
            Delete
          </span>
          <span>Omission</span>
        </div>
        <div style={drawerStyles.legendItem}>
          <span
            style={{
              ...drawerStyles.errorInsert,
              padding: '1px 4px',
              fontSize: '9px',
            }}
          >
            Insert
          </span>
          <span>Insertion</span>
        </div>
      </div>
    </div>
  )

  const renderLoadingState = () => (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <Spin size="large" />
      <Text style={{ display: 'block', marginTop: '16px', color: '#666' }}>
        Loading assessment report...
      </Text>
    </div>
  )

  const renderErrorState = () => (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Text style={{ color: '#ff4d4f', fontSize: '14px' }}>
            Error loading report. Please try again later.
          </Text>
        }
      />
    </div>
  )

  const renderEmptyState = () => (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Text style={{ color: '#666', fontSize: '14px' }}>
            Report not available yet. Reports will be generated soon.
          </Text>
        }
      />
    </div>
  )

  const renderScoreCard = (title, score, subtitle = null) => (
    <Card
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: '8px',
        boxShadow: 'none',
      }}
      bodyStyle={{ padding: '20px' }}
    >
      <div style={{ textAlign: 'center' }}>
        <Text
          style={{
            fontSize: '14px',
            color: '#333',
            fontWeight: '500',
            display: 'block',
            marginBottom: '4px',
          }}
        >
          {title}
        </Text>
        {subtitle && (
          <Text
            style={{
              fontSize: '12px',
              color: '#999',
              display: 'block',
              marginBottom: '16px',
            }}
          >
            {subtitle}
          </Text>
        )}
        <div style={{ marginBottom: '12px' }}>
          <Text
            style={{
              fontSize: '28px',
              fontWeight: '600',
              color: getScoreColor(score),
            }}
          >
            {score || 0}%
          </Text>
        </div>
        <Progress
          percent={score || 0}
          strokeColor={getScoreColor(score)}
          trailColor="#f5f5f5"
          showInfo={false}
          strokeWidth={6}
        />
        <Text
          style={{
            fontSize: '12px',
            color: getScoreColor(score),
            fontWeight: '500',
            display: 'block',
            marginTop: '8px',
          }}
        >
          {getScoreLevel(score || 0)}
        </Text>
      </div>
    </Card>
  )

  const renderDetailedReadingScores = readingMetadata => {
    if (!readingMetadata) return null

    const {
      accuracy_score = 0,
      completeness_score = 0,
      coverage_percentage = 0,
      fluency_score = 0,
      pronunciation_score = 0,
      prosody_score = 0,
      total_score = 0,
      error_counts = {},
      transcription = '',
    } = readingMetadata

    return (
      <div style={{ marginTop: '32px' }}>
        <Title
          level={5}
          style={{
            fontSize: '16px',
            color: '#1890ff',
            marginBottom: '20px',
            fontWeight: '600',
          }}
        >
          Reading Analysis
        </Title>

        {/* Overall Reading Score */}
        <Card
          style={{
            marginBottom: '24px',
            border: '2px solid #1890ff',
            borderRadius: '8px',
          }}
        >
          <div style={{ textAlign: 'center', padding: '8px' }}>
            <Text
              style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#333',
                display: 'block',
                marginBottom: '8px',
              }}
            >
              Overall Reading Score
            </Text>
            <Text
              style={{
                fontSize: '36px',
                fontWeight: '600',
                color: getScoreColor(total_score),
                display: 'block',
              }}
            >
              {total_score.toFixed(1)}%
            </Text>
            <Text
              style={{
                fontSize: '14px',
                color: getScoreColor(total_score),
                fontWeight: '500',
              }}
            >
              {getScoreLevel(total_score)}
            </Text>
          </div>
        </Card>

        {/* Detailed Scores Grid */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {[
            { label: 'Accuracy', score: accuracy_score },
            { label: 'Completeness', score: completeness_score },
            { label: 'Fluency', score: fluency_score },
            { label: 'Pronunciation', score: pronunciation_score },
            { label: 'Prosody', score: prosody_score },
            { label: 'Coverage', score: coverage_percentage },
          ].map((item, index) => (
            <Col xs={12} sm={8} key={index}>
              <Card
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                  height: '100%',
                }}
              >
                <div style={{ textAlign: 'center', padding: '4px' }}>
                  <Text
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    {item.label}
                  </Text>
                  <Text
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: getScoreColor(item.score),
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    {item.score.toFixed(1)}%
                  </Text>
                  <Progress
                    percent={item.score}
                    strokeColor={getScoreColor(item.score)}
                    trailColor="#f5f5f5"
                    size="small"
                    showInfo={false}
                    strokeWidth={4}
                  />
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Error Analysis */}
        {error_counts && Object.keys(error_counts).length > 0 && (
          <Card
            style={{
              marginBottom: '24px',
              border: '1px solid #ff4d4f',
              borderRadius: '8px',
            }}
          >
            <Title
              level={5}
              style={{
                fontSize: '14px',
                color: '#ff4d4f',
                marginBottom: '16px',
                fontWeight: '600',
              }}
            >
              Error Analysis
            </Title>
            <Row gutter={[16, 16]}>
              {[
                {
                  label: 'Pronunciation',
                  count: error_counts.pronunciation_errors || 0,
                  color: '#ff4d4f',
                },
                {
                  label: 'Omission',
                  count: error_counts.omission_errors || 0,
                  color: '#1890ff',
                },
                {
                  label: 'Insertion',
                  count: error_counts.insertion_errors || 0,
                  color: '#52c41a',
                },
              ].map((error, index) => (
                <Col span={8} key={index}>
                  <div style={{ textAlign: 'center' }}>
                    <Text
                      style={{
                        fontSize: '24px',
                        color: error.color,
                        fontWeight: '600',
                        display: 'block',
                      }}
                    >
                      {error.count}
                    </Text>
                    <Text
                      style={{
                        fontSize: '12px',
                        color: '#666',
                      }}
                    >
                      {error.label} Errors
                    </Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        {/* Transcription */}
        {transcription && (
          <Card style={{ border: '1px solid #e9ecef', borderRadius: '6px' }}>
            <Title
              level={5}
              style={{
                fontSize: '13px',
                color: '#495057',
                marginBottom: '10px',
                fontWeight: '500',
              }}
            >
              Reading Transcription
            </Title>

            {/* Error Legend */}
            {renderErrorLegend()}

            <div style={drawerStyles.transcriptionContainer}>
              <div style={drawerStyles.transcriptionText}>
                {parseTranscriptionWithErrors(transcription).map(
                  (part, index) => {
                    if (part.type === 'error') {
                      const errorStyle =
                        part.cssType === 'replace'
                          ? drawerStyles.errorReplace
                          : part.cssType === 'delete'
                          ? drawerStyles.errorDelete
                          : drawerStyles.errorInsert

                      return (
                        <span
                          key={index}
                          style={{
                            ...errorStyle,
                            display: 'inline-block',
                            maxWidth: '100%',
                          }}
                          title={`${part.errorType}: ${part.text}`}
                        >
                          {part.text}
                        </span>
                      )
                    }

                    return (
                      <span key={index} style={drawerStyles.normalText}>
                        {part.text}
                      </span>
                    )
                  }
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    )
  }

  const renderVideoAnalysis = englishLangData => {
    if (!englishLangData || !englishLangData.detailed_scores) return null

    const videoScore = englishLangData.detailed_scores[0]
    if (!videoScore) return null

    const { overall_score = 0, detailed_analysis = {} } = videoScore
    const isNonEnglish = videoScore.isNonEnglish || false

    const {
      fluency_score = 0,
      grammar_score = 0,
      accuracy_score = 0,
      prosody_score = 0,
      vocabulary_score = 0,
      coherence_score = 0,
      transcription = '',
    } = detailed_analysis

    const videoMetrics = isNonEnglish
      ? [
          { label: 'Fluency', score: fluency_score * 100 },
          { label: 'Grammar', score: grammar_score * 100 },
          { label: 'Vocabulary', score: vocabulary_score * 100 },
          { label: 'Coherence', score: coherence_score * 100 },
        ]
      : [
          { label: 'Fluency', score: fluency_score * 100 },
          { label: 'Grammar', score: grammar_score * 100 },
          { label: 'Vocabulary', score: vocabulary_score * 100 },
          { label: 'Pronunciation', score: accuracy_score * 100 },
        ]

    return (
      <div style={{ marginTop: '32px' }}>
        <Title
          level={5}
          style={{
            fontSize: '16px',
            color: '#1890ff',
            marginBottom: '20px',
            fontWeight: '600',
          }}
        >
          Video Response Analysis
        </Title>

        {/* Overall Score */}
        <Card
          style={{
            marginBottom: '24px',
            border: '2px solid #1890ff',
            borderRadius: '8px',
          }}
        >
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Text
              style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#333',
                display: 'block',
                marginBottom: '8px',
              }}
            >
              Overall Score
            </Text>
            <Text
              style={{
                fontSize: '36px',
                fontWeight: '600',
                color: getScoreColor(overall_score * 100),
                display: 'block',
              }}
            >
              {(overall_score * 100).toFixed(1)}%
            </Text>
            <Text
              style={{
                fontSize: '14px',
                color: getScoreColor(overall_score * 100),
                fontWeight: '500',
              }}
            >
              {getScoreLevel(overall_score * 100)}
            </Text>
          </div>
        </Card>

        {/* Detailed Video Scores */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {videoMetrics.map((item, index) => (
            <Col xs={12} sm={6} key={index}>
              <Card
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                  height: '100%',
                }}
              >
                <div style={{ textAlign: 'center', padding: '4px' }}>
                  <Text
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    {item.label}
                  </Text>
                  <Text
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: getScoreColor(item.score),
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    {item.score.toFixed(1)}%
                  </Text>
                  <Progress
                    percent={item.score}
                    strokeColor={getScoreColor(item.score)}
                    trailColor="#f5f5f5"
                    size="small"
                    showInfo={false}
                    strokeWidth={4}
                  />
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Video Transcription */}
        {transcription && (
          <Card style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}>
            <Title
              level={5}
              style={{
                fontSize: '14px',
                color: '#1890ff',
                marginBottom: '12px',
                fontWeight: '600',
              }}
            >
              Video Response Transcription
            </Title>
            <div
              style={{
                padding: '16px',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #f0f0f0',
              }}
            >
              <Text
                style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#333',
                }}
              >
                {transcription.replace(/#/g, '__')}
              </Text>
            </div>
          </Card>
        )}
      </div>
    )
  }

  // Utility functions for aptitude results
  const formatTimeDetailed = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const getAverageTimePerQuestion = category => {
    const totalQuestions = category.topics.reduce(
      (sum, topic) => sum + (topic.right + topic.wrong + topic.unattempted),
      0
    )
    if (totalQuestions === 0) return 0
    return Math.round(category.time_spent_seconds / totalQuestions)
  }

  const formatPercentageCell = (value, percentage) => (
    <span
      style={{
        fontWeight: '600',
        color:
          percentage >= 80
            ? '#10b981'
            : percentage >= 60
            ? '#f59e0b'
            : percentage >= 40
            ? '#ef4444'
            : '#6b7280',
      }}
    >
      {percentage}%
    </span>
  )

  const formatScoreCell = (gained, total) => (
    <span style={{ fontWeight: '600', color: '#262626' }}>
      {gained} / {total}
    </span>
  )

  const renderAptitudeAssessment = assessmentReport => {
    const {
      totalScore = 0,
      gainedScore = 0,
      categories = [],
      timestamp,
    } = assessmentReport

    const overallPercentage =
      totalScore > 0 ? (gainedScore / totalScore) * 100 : 0

    // Calculate statistics similar to assessment-react
    const statistics = {
      summary: {
        total_questions: categories.reduce(
          (sum, cat) =>
            sum +
            cat.topics.reduce(
              (topicSum, topic) =>
                topicSum + (topic.right + topic.wrong + topic.unattempted),
              0
            ),
          0
        ),
        total_attempted: categories.reduce(
          (sum, cat) =>
            sum +
            cat.topics.reduce(
              (topicSum, topic) => topicSum + (topic.right + topic.wrong),
              0
            ),
          0
        ),
        total_right: categories.reduce(
          (sum, cat) =>
            sum +
            cat.topics.reduce((topicSum, topic) => topicSum + topic.right, 0),
          0
        ),
        total_wrong: categories.reduce(
          (sum, cat) =>
            sum +
            cat.topics.reduce((topicSum, topic) => topicSum + topic.wrong, 0),
          0
        ),
        total_unattempted: categories.reduce(
          (sum, cat) =>
            sum +
            cat.topics.reduce(
              (topicSum, topic) => topicSum + topic.unattempted,
              0
            ),
          0
        ),
        total_time_seconds: 3600, // Default value
        average_speed_seconds_per_question: 60, // Default value
        easy_gained: Math.floor(gainedScore * 0.4),
        easy_total: Math.floor(totalScore * 0.4),
        medium_gained: Math.floor(gainedScore * 0.4),
        medium_total: Math.floor(totalScore * 0.4),
        hard_gained: Math.floor(gainedScore * 0.2),
        hard_total: Math.floor(totalScore * 0.2),
      },
      categories: categories,
    }

    const accuracy =
      statistics.summary.total_questions > 0
        ? Math.round(
            (statistics.summary.total_right /
              statistics.summary.total_questions) *
              100
          )
        : 0

    // Prepare data for subject analysis table
    const subjectColumns = [
      {
        title: 'Subject',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        width: '25%',
      },
      {
        title: 'Score',
        key: 'score',
        width: '15%',
        render: (_, record) =>
          formatScoreCell(record.gained_marks, record.total_marks),
      },
      {
        title: 'Percentage',
        key: 'percentage',
        width: '15%',
        render: (_, record) => {
          const percentage =
            record.total_marks > 0
              ? Math.round((record.gained_marks / record.total_marks) * 100)
              : 0
          return formatPercentageCell(record.gained_marks, percentage)
        },
      },
      {
        title: 'Attempted',
        key: 'attempted',
        width: '15%',
        render: (_, record) => {
          const totalQuestions = record.topics.reduce(
            (sum, topic) =>
              sum + (topic.right + topic.wrong + topic.unattempted),
            0
          )
          return totalQuestions
        },
      },
      {
        title: 'Correct',
        key: 'correct',
        width: '15%',
        render: (_, record) => {
          const totalCorrect = record.topics.reduce(
            (sum, topic) => sum + topic.right,
            0
          )
          return totalCorrect
        },
      },
      {
        title: 'Avg Time',
        key: 'avgTime',
        width: '15%',
        render: (_, record) => `${getAverageTimePerQuestion(record)}s`,
      },
    ]

    const subjectData = categories.map((category, index) => ({
      key: index,
      ...category,
      time_spent_seconds: 1800, // Default value
    }))

    return (
      <DrawerContainer>
        <ContentSection>
          {/* Header Section */}
          <div
            style={{
              marginBottom: '24px',
              textAlign: 'center',
              padding: '0 8px',
            }}
          >
            <Text
              style={{
                color: '#8c8c8c',
                fontSize: '12px',
                display: 'block',
                marginTop: '4px',
              }}
            >
              Completed on{' '}
              {moment(timestamp).format('MMM DD, YYYY [at] h:mm A')}
            </Text>
          </div>

          {/* Overall Score Cards */}
          <OverallScoreGrid>
            <ScoreCard>
              <ScoreValue>
                {gainedScore}/{totalScore}
              </ScoreValue>
              <ScoreLabel>Overall Score</ScoreLabel>
              <ScoreSubtext>{overallPercentage.toFixed(1)}%</ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>{statistics.summary.total_attempted}</ScoreValue>
              <ScoreLabel>Questions Attempted</ScoreLabel>
              <ScoreSubtext>
                out of {statistics.summary.total_questions}
              </ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>{statistics.summary.total_right}</ScoreValue>
              <ScoreLabel>Correct Answers</ScoreLabel>
              <ScoreSubtext>{accuracy}% accuracy</ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>
                {formatTimeDetailed(statistics.summary.total_time_seconds)}
              </ScoreValue>
              <ScoreLabel>Time Efficiency</ScoreLabel>
              <ScoreSubtext>
                {statistics.summary.average_speed_seconds_per_question.toFixed(
                  1
                )}
                s avg per question
              </ScoreSubtext>
            </ScoreCard>
          </OverallScoreGrid>

          {/* Summary Stats */}
          <SummaryGrid>
            <SummaryCard>
              <SummaryValue>{statistics.summary.total_questions}</SummaryValue>
              <SummaryLabel>Total Questions</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{statistics.summary.total_right}</SummaryValue>
              <SummaryLabel>Correct</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{statistics.summary.total_wrong}</SummaryValue>
              <SummaryLabel>Incorrect</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>
                {statistics.summary.total_unattempted}
              </SummaryValue>
              <SummaryLabel>Unattempted</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>
                {formatTimeDetailed(statistics.summary.total_time_seconds)}
              </SummaryValue>
              <SummaryLabel>Total Time</SummaryLabel>
            </SummaryCard>
          </SummaryGrid>

          {/* Performance by Difficulty Level */}
          <div style={{ marginBottom: '24px' }}>
            <SectionTitle>Performance by Difficulty Level</SectionTitle>
            <DifficultyGrid>
              <DifficultyCard borderColor="#bbf7d0" bgColor="#f0fdf4">
                <DifficultyLabel>Easy Questions</DifficultyLabel>
                <DifficultyScore color="#16a34a">
                  {statistics.summary.easy_gained} /{' '}
                  {statistics.summary.easy_total}
                </DifficultyScore>
                <DifficultyPercentage>
                  {statistics.summary.easy_total > 0
                    ? Math.round(
                        (statistics.summary.easy_gained /
                          statistics.summary.easy_total) *
                          100
                      )
                    : 0}
                  %
                </DifficultyPercentage>
              </DifficultyCard>
              <DifficultyCard borderColor="#fed7aa" bgColor="#fffbeb">
                <DifficultyLabel>Medium Questions</DifficultyLabel>
                <DifficultyScore color="#ea580c">
                  {statistics.summary.medium_gained} /{' '}
                  {statistics.summary.medium_total}
                </DifficultyScore>
                <DifficultyPercentage>
                  {statistics.summary.medium_total > 0
                    ? Math.round(
                        (statistics.summary.medium_gained /
                          statistics.summary.medium_total) *
                          100
                      )
                    : 0}
                  %
                </DifficultyPercentage>
              </DifficultyCard>
              <DifficultyCard borderColor="#fecaca" bgColor="#fef2f2">
                <DifficultyLabel>Hard Questions</DifficultyLabel>
                <DifficultyScore color="#dc2626">
                  {statistics.summary.hard_gained} /{' '}
                  {statistics.summary.hard_total}
                </DifficultyScore>
                <DifficultyPercentage>
                  {statistics.summary.hard_total > 0
                    ? Math.round(
                        (statistics.summary.hard_gained /
                          statistics.summary.hard_total) *
                          100
                      )
                    : 0}
                  %
                </DifficultyPercentage>
              </DifficultyCard>
            </DifficultyGrid>
          </div>

          {/* Detailed Subject Analysis Table */}
          <div style={{ marginBottom: '24px' }}>
            <SectionTitle>Detailed Subject Analysis</SectionTitle>
            <ResponsiveTableContainer>
              <Table
                columns={subjectColumns}
                dataSource={subjectData}
                pagination={false}
                size="small"
                scroll={{ x: 600 }}
                bordered={false}
              />
            </ResponsiveTableContainer>
          </div>

          {/* Subject-wise Topic Breakdown Tables */}
          {categories.map((category, categoryIndex) => {
            const topicColumns = [
              {
                title: 'Topic',
                dataIndex: 'name',
                key: 'name',
                ellipsis: true,
                width: '30%',
              },
              {
                title: 'Score',
                key: 'score',
                width: '12%',
                render: (_, record) =>
                  formatScoreCell(record.gained, record.total),
              },
              {
                title: '%',
                key: 'percentage',
                width: '10%',
                render: (_, record) => {
                  const percentage =
                    record.total > 0
                      ? Math.round((record.gained / record.total) * 100)
                      : 0
                  return formatPercentageCell(record.gained, percentage)
                },
              },
              {
                title: 'Attempted',
                key: 'attempted',
                width: '12%',
                render: (_, record) => record.right + record.wrong,
              },
              {
                title: 'Correct',
                dataIndex: 'right',
                key: 'right',
                width: '12%',
              },
              {
                title: 'Wrong',
                dataIndex: 'wrong',
                key: 'wrong',
                width: '12%',
              },
              {
                title: 'Skipped',
                dataIndex: 'unattempted',
                key: 'unattempted',
                width: '12%',
              },
            ]

            const topicData = category.topics.map((topic, index) => ({
              key: `${categoryIndex}-${index}`,
              ...topic,
            }))

            return (
              <div key={categoryIndex} style={{ marginBottom: '24px' }}>
                <SectionTitle>{category.name} - Topic Breakdown</SectionTitle>
                <ResponsiveTableContainer>
                  <Table
                    columns={topicColumns}
                    dataSource={topicData}
                    pagination={false}
                    size="small"
                    scroll={{ x: 700 }}
                    bordered={false}
                  />
                </ResponsiveTableContainer>
              </div>
            )
          })}
        </ContentSection>
      </DrawerContainer>
    )
  }

  const renderScoreContent = () => {
    if (loading) return renderLoadingState()
    if (error) return renderErrorState()

    const assessmentReport = findAssessmentReport()
    if (!assessmentReport) return renderEmptyState()

    // Handle communication and hinglish assessments
    const isHinglishReport = assessmentReport.type?.toLowerCase() === 'hinglish'
    if (assessmentReport.type?.toLowerCase() === 'communication' || isHinglishReport) {
      // Map API field names to display names
      // API returns: speakingScore, readingScore, listeningScore, videoResponseScore, paragraphReadingScore, audioQuestionScore
      const englishLang = assessmentReport.speakingScore ?? assessmentReport.englishLang ?? 0
      const readingAbility = assessmentReport.readingScore ?? assessmentReport.readingAbility ?? 0
      const comprehension = assessmentReport.listeningScore ?? assessmentReport.comprehension ?? 0

      // Build metadata from section score objects returned by API
      const metadata = assessmentReport.metadata || {}
      // Map new API section scores into metadata format expected by detailed renderers
      if (assessmentReport.videoResponseScore && !metadata.englishLang) {
        metadata.englishLang = assessmentReport.videoResponseScore
      }
      if (assessmentReport.paragraphReadingScore && !metadata.readingAbility) {
        metadata.readingAbility = { reading: assessmentReport.paragraphReadingScore }
      }
      if (assessmentReport.audioQuestionScore && !metadata.comprehension) {
        metadata.comprehension = assessmentReport.audioQuestionScore
      }

      return (
        <div style={{ margin: '0 16px' }}>
          {/* Header Section */}
          <div
            style={{
              marginBottom: '32px',
              textAlign: 'center',
              padding: '0 16px',
            }}
          >
            <Title
              level={4}
              style={{
                margin: 0,
                color: '#1890ff',
                fontSize: '20px',
                fontWeight: '600',
              }}
            >
              Communication Assessment Results
            </Title>
            <Text
              style={{
                color: '#666',
                fontSize: '14px',
                display: 'block',
                marginTop: '8px',
              }}
            >
              Completed on{' '}
              {moment(assessmentReport.timestamp).format(
                'MMM DD, YYYY [at] h:mm A'
              )}
            </Text>
          </div>

          {/* Main Scores Section */}
          <Row
            gutter={[16, 16]}
            style={{ marginBottom: '32px', padding: '0 8px' }}
          >
            <Col xs={24} sm={8}>
              {renderScoreCard(
                'Speaking Ability',
                englishLang,
                // 'Speaking & Grammar'
              )}
            </Col>
            <Col xs={24} sm={8}>
              {renderScoreCard(
                'Reading Ability',
                Math.min(readingAbility, 100),
                // 'Pronunciation & Fluency'
              )}
            </Col>
            <Col xs={24} sm={8}>
              {renderScoreCard(
                'Listening Ability',
                comprehension,
                // 'Listening & Understanding'
              )}
            </Col>
          </Row>

          <Divider style={{ margin: '32px 16px' }} />

          {/* Video Response Analysis */}
          <div style={{ padding: '0 8px' }}>
            {metadata.englishLang && renderVideoAnalysis(metadata.englishLang)}

            {/* Reading Analysis */}
            {metadata.readingAbility?.reading &&
              renderDetailedReadingScores(metadata.readingAbility.reading)}

            {/* Comprehension Section */}
            {metadata.comprehension && (
              <div style={{ marginTop: '32px' }}>
                <Title
                  level={5}
                  style={{
                    fontSize: '16px',
                    color: '#1890ff',
                    marginBottom: '20px',
                    fontWeight: '600',
                    paddingLeft: '8px',
                  }}
                >
                  Audio Comprehension Analysis
                </Title>
                <Card
                  style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}
                >
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text
                      style={{
                        fontSize: '16px',
                        color: '#333',
                        display: 'block',
                        marginBottom: '16px',
                      }}
                    >
                      Correct Answers:{' '}
                      {metadata.comprehension.correct_answers || 0} /{' '}
                      {metadata.comprehension.total_questions || 0}
                    </Text>
                    <Progress
                      percent={parseFloat(
                        (metadata.comprehension.total_questions
                          ? (metadata.comprehension.correct_answers /
                              metadata.comprehension.total_questions) *
                            100
                          : 0
                        ).toFixed(2)
                      )}
                      strokeColor={getScoreColor(
                        metadata.comprehension.total_questions
                          ? (metadata.comprehension.correct_answers /
                              metadata.comprehension.total_questions) *
                              100
                          : 0
                      )}
                      trailColor="#f5f5f5"
                      strokeWidth={8}
                    />
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      )
    }

    // Handle aptitude assessments
    if (assessmentReport.type?.toLowerCase() === 'aptitude') {
      return renderAptitudeAssessment(assessmentReport)
    }

    return renderEmptyState()
  }

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Text
            style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#333',
            }}
          >
            Assessment Report
          </Text>
        </div>
      }
      placement="right"
      width={Math.min(700, window.innerWidth * 0.85)}
      onClose={onClose}
      open={visible}
      closeIcon={<CloseOutlined style={{ fontSize: '16px', color: '#666' }} />}
      styles={{
        header: {
          borderBottom: '1px solid #f0f0f0',
          padding: '16px 24px',
        },
        body: {
          padding: '16px 8px 24px 8px',
          backgroundColor: '#fafafa',
        },
      }}
    >
      {renderScoreContent()}
    </Drawer>
  )
}

export default ScoreDrawer
          